import React, { useState, useEffect } from "react";
import {
  UploadOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  LoadingOutlined,
  PlusOutlined,
  FilterOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { Tabs, App, Select } from "antd";
import * as XLSX from "xlsx";
import {
  fetchContainers,
  createContainerBatch,
  deleteContainerBatch,
  uploadContainerAuxPower,
  uploadContainerPerformance,
  fetchContainerPerformances,
  fetchContainerAuxPowers,
  createContainerPerformance,
  createContainerAuxPower,
  updateContainerAuxPower,
  deleteContainerAuxPower,
  updateContainerPerformance,
  deleteContainerPerformance,
  BASE_URL,
  customFetch,
} from "../../services/bessApi";

export default function BessContainerPage() {
  const { message } = App.useApp();
  const [containers, setContainers] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadContainers = async () => {
    try {
      setLoading(true);
      const data = await fetchContainers();
      setContainers(data);
    } catch (err) {
      message.error("Failed to load BESS containers: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContainers();
  }, []);

  const tabItems = [
    {
      key: "bess_container",
      label: <span className="font-semibold text-sm">BESS Container</span>,
      children: (
        <BessContainerTab
          containers={containers}
          loading={loading}
          onSuccess={loadContainers}
        />
      ),
    },
    {
      key: "container_performance",
      label: <span className="font-semibold text-sm">Container Performance</span>,
      children: (
        <ContainerPerformanceTab
          containers={containers}
          loadingContainers={loading}
        />
      ),
    },
    {
      key: "container_aux",
      label: <span className="font-semibold text-sm">Container AUX Power</span>,
      children: (
        <ContainerAuxPowerTab
          containers={containers}
          loadingContainers={loading}
        />
      ),
    },
  ];

  return (
    <div className="bess-container-redesign min-h-screen p-6 flex flex-col">
      {/* Styles Block for Redesign overriding index.html global styles with higher specificity */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Hanken+Grotesk:ital,wght@0,100..900;1,100..900&family=Geist:wght@100..900&family=Inter:ital,wght@0,100..900;1,100..900&display=swap');

        .bess-container-redesign {
          background-color: #f7f9fb !important;
          font-family: 'Inter', sans-serif !important;
          color: #191c1e !important;
        }

        .bess-container-redesign h1,
        .bess-container-redesign h2,
        .bess-container-redesign h3,
        .bess-container-redesign h4 {
          font-family: 'Hanken Grotesk', sans-serif !important;
          font-weight: 600 !important;
          border-left: none !important;
          padding-left: 0 !important;
          text-transform: none !important;
          margin: 0 !important;
          letter-spacing: -0.01em !important;
        }

        .bess-container-redesign .headline-lg {
          font-size: 32px !important;
          line-height: 40px !important;
          letter-spacing: -0.02em !important;
        }

        .bess-container-redesign .headline-md {
          font-size: 24px !important;
          line-height: 32px !important;
        }

        .bess-container-redesign .headline-sm {
          font-size: 18px !important;
          line-height: 24px !important;
        }

        /* Labels override from global index.html style */
        .bess-layout-content .bess-container-redesign label {
          color: #3f4850 !important;
          font-family: 'Inter', sans-serif !important;
          font-size: 13px !important;
          font-weight: 500 !important;
          text-transform: none !important;
          letter-spacing: normal !important;
          margin-bottom: 6px !important;
          display: block !important;
        }

        /* Inputs/select overrides from global index.html style */
        .bess-layout-content .bess-container-redesign input:not([type="checkbox"]):not([type="radio"]),
        .bess-layout-content .bess-container-redesign select {
          height: 44px !important;
          padding: 0 14px !important;
          font-size: 14px !important;
          font-family: 'Inter', sans-serif !important;
          font-weight: 400 !important;
          color: #191c1e !important;
          border: 1px solid #bfc7d2 !important;
          border-radius: 8px !important;
          background-color: rgba(248, 250, 252, 0.5) !important;
          transition: all 0.2s ease-in-out !important;
          box-shadow: none !important;
          box-sizing: border-box !important;
          width: 100% !important;
        }

        .bess-layout-content .bess-container-redesign input:not([type="checkbox"]):not([type="radio"]):hover,
        .bess-layout-content .bess-container-redesign select:hover {
          border-color: #707881 !important;
        }

        .bess-layout-content .bess-container-redesign input:not([type="checkbox"]):not([type="radio"]):focus,
        .bess-layout-content .bess-container-redesign select:focus {
          border-color: #006194 !important;
          background-color: #ffffff !important;
          box-shadow: 0 0 0 3px #dbeafe !important;
          outline: none !important;
        }

        /* Premium Card style */
        .bess-card {
          background-color: #ffffff !important;
          border-radius: 10px !important; /* xl rounded corner */
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.02) !important;
          padding: 24px !important;
          border: 1px solid #eceef0 !important;
          box-sizing: border-box !important;
        }

        /* Style Ant Design Select dropdown to look incredibly premium with curves */
        .bess-container-redesign .bess-antd-select.ant-select .ant-select-selector {
          background-color: rgba(248, 250, 252, 0.5) !important;
          border: 1px solid #bfc7d2 !important;
          border-radius: 8px !important;
          height: 40px !important;
          display: flex !important;
          align-items: center !important;
          transition: all 0.2s ease-in-out !important;
          box-shadow: none !important;
        }
        
        .bess-container-redesign .bess-antd-select.ant-select:hover .ant-select-selector {
          border-color: #707881 !important;
        }
        
        .bess-container-redesign .bess-antd-select.ant-select-focused .ant-select-selector {
          border-color: #006194 !important;
          background-color: #ffffff !important;
          box-shadow: 0 0 0 3px #dbeafe !important;
        }

        .ant-select-dropdown {
          border-radius: 12px !important;
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.04) !important;
          border: 1px solid #f1f5f9 !important;
          padding: 6px !important;
          z-index: 10000 !important;
        }

        .ant-select-dropdown .ant-select-item {
          border-radius: 6px !important;
          padding: 8px 12px !important;
          font-size: 14px !important;
          font-family: 'Inter', sans-serif !important;
          color: #191c1e !important;
        }

        .ant-select-dropdown .ant-select-item-option-selected {
          background-color: #f0f9ff !important;
          color: #006194 !important;
          font-weight: 600 !important;
        }

        .ant-select-dropdown .ant-select-item-option-active {
          background-color: #f8fafc !important;
        }

        /* AntD Tab styling overrides */
        .bess-container-redesign .premium-tabs {
          margin: 0 !important;
          padding: 0 !important;
        }

        .bess-container-redesign .premium-tabs .ant-tabs-nav {
          background-color: #f7f9fb !important;
          border-bottom: 1px solid #e0e3e5 !important;
          margin-bottom: 24px !important;
          padding: 0 !important;
        }

        .bess-container-redesign .premium-tabs .ant-tabs-tab {
          padding: 12px 16px !important;
          margin: 0 16px 0 0 !important;
          font-family: 'Inter', sans-serif !important;
          font-size: 14px !important;
          font-weight: 500 !important;
          color: #3f4850 !important;
          transition: all 0.2s !important;
        }

        .bess-container-redesign .premium-tabs .ant-tabs-tab:hover {
          color: #006194 !important;
        }

        .bess-container-redesign .premium-tabs .ant-tabs-tab-active {
          color: #006194 !important;
        }

        .bess-container-redesign .premium-tabs .ant-tabs-tab-active .ant-tabs-tab-btn {
          color: #006194 !important;
          font-weight: 600 !important;
        }

        .bess-container-redesign .premium-tabs .ant-tabs-ink-bar {
          background: #006194 !important;
          height: 2px !important;
        }

        /* Buttons */
        .bess-btn-primary {
          background-color: #006194 !important;
          color: #ffffff !important;
          font-family: 'Inter', sans-serif !important;
          font-weight: 600 !important;
          font-size: 14px !important;
          border: none !important;
          border-radius: 8px !important;
          height: 44px !important;
          cursor: pointer !important;
          transition: background-color 0.2s ease-in-out !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 8px !important;
        }

        .bess-btn-primary:hover {
          background-color: #007bb9 !important;
        }

        .bess-btn-primary:disabled {
          opacity: 0.5 !important;
          cursor: not-allowed !important;
        }

        .bess-btn-secondary {
          background-color: #f2f4f6 !important;
          color: #191c1e !important;
          font-family: 'Inter', sans-serif !important;
          font-weight: 500 !important;
          font-size: 14px !important;
          border: 1px solid #e6e8ea !important;
          border-radius: 8px !important;
          height: 44px !important;
          cursor: pointer !important;
          transition: all 0.2s ease-in-out !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 8px !important;
        }

        .bess-btn-secondary:hover {
          background-color: #e6e8ea !important;
        }

        /* Upload zone */
        .bess-upload-zone {
          border: 2px dashed #bfc7d2 !important;
          border-radius: 10px !important;
          padding: 32px !important;
          background-color: #ffffff !important;
          text-align: center !important;
          display: flex !important;
          flex-direction: column !important;
          align-items: center !important;
          justify-content: center !important;
          transition: all 0.2s ease-in-out !important;
          cursor: pointer !important;
        }

        .bess-upload-zone.active {
          border-color: #006194 !important;
          background-color: rgba(219, 234, 254, 0.2) !important;
        }

        .bess-upload-zone:hover {
          border-color: #006194 !important;
        }

        /* Download template button green */
        .bess-btn-download {
          background-color: #6cf8bb !important;
          color: #00714d !important;
          font-family: 'Inter', sans-serif !important;
          font-weight: 600 !important;
          font-size: 13px !important;
          border: none !important;
          border-radius: 8px !important;
          height: 38px !important;
          padding: 0 16px !important;
          cursor: pointer !important;
          transition: opacity 0.2s !important;
          display: inline-flex !important;
          align-items: center !important;
          gap: 8px !important;
        }

        .bess-btn-download:hover {
          opacity: 0.9 !important;
        }

        /* Technical unit badges */
        .tech-badge {
          font-family: 'Geist', monospace !important;
          font-size: 10px !important;
          font-weight: 600 !important;
          background-color: #f2f4f6 !important;
          color: #707881 !important;
          padding: 2px 6px !important;
          border-radius: 4px !important;
          text-transform: uppercase !important;
          display: inline-block !important;
          margin-left: 6px !important;
        }



        /* Table styles */
        .bess-table {
          width: 100% !important;
          border-collapse: collapse !important;
        }

        .bess-table th {
          background-color: #f8fafc !important;
          font-family: 'Geist', sans-serif !important;
          font-size: 12px !important;
          font-weight: 500 !important;
          color: #707881 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.05em !important;
          padding: 12px 16px !important;
          border-bottom: 1px solid #e6e8ea !important;
          text-align: left !important;
          position: sticky !important;
          top: 0 !important;
          z-index: 10 !important;
        }

        .bess-table td {
          padding: 14px 16px !important;
          border-bottom: 1px solid #f2f4f6 !important;
          font-size: 14px !important;
          font-family: 'Inter', sans-serif !important;
          color: #191c1e !important;
        }

        .bess-table tr:hover td {
          background-color: rgba(248, 250, 252, 0.5) !important;
        }

        .bess-table-actions {
          display: flex !important;
          gap: 12px !important;
          justify-content: center !important;
        }

        .bess-table th.text-center,
        .bess-table td.text-center {
          text-align: center !important;
        }

        .bess-table th.bess-sticky-col {
          position: sticky !important;
          right: 0 !important;
          background-color: #f8fafc !important;
          z-index: 20 !important;
          box-shadow: -4px 0 8px -2px rgba(0, 0, 0, 0.06) !important;
        }

        .bess-table td.bess-sticky-col {
          position: sticky !important;
          right: 0 !important;
          background-color: #ffffff !important;
          z-index: 15 !important;
          box-shadow: -4px 0 8px -2px rgba(0, 0, 0, 0.06) !important;
        }

        .bess-table tr:hover td.bess-sticky-col {
          background-color: #f8fafc !important;
        }

        .bess-action-link {
          color: #006194 !important;
          font-weight: 600 !important;
          cursor: pointer !important;
          font-size: 13px !important;
          text-decoration: none !important;
          background: none !important;
          border: none !important;
          padding: 0 !important;
        }

        .bess-action-link:hover {
          color: #007bb9 !important;
          text-decoration: underline !important;
        }

        .bess-action-link.secondary {
          color: #707881 !important;
        }

        .bess-action-link.secondary:hover {
          color: #191c1e !important;
        }

        .bess-action-link.danger {
          color: #dc2626 !important;
        }

        .bess-action-link.danger:hover {
          color: #b91c1c !important;
          text-decoration: underline !important;
        }

        /* Geist font alignment for data values */
        .geist-val {
          font-family: 'Geist', monospace !important;
          font-weight: 500 !important;
        }
      `}</style>

      <div className="max-w-[1440px] mx-auto w-full flex-1">
        <Tabs
          defaultActiveKey="bess_container"
          items={tabItems}
          className="premium-tabs"
        />
      </div>
    </div>
  );
}

// ==========================================
// 1. BESS Container Tab Component
// ==========================================
function BessContainerTab({ containers, loading, onSuccess }) {
  const { message, modal } = App.useApp();

  // Manual form state
  const [oem, setOem] = useState("");
  const [model, setModel] = useState("");
  const [capacity, setCapacity] = useState("");
  const [savingManual, setSavingManual] = useState(false);

  // Excel upload state
  const [parsedData, setParsedData] = useState([]);
  const [fileName, setFileName] = useState("");
  const [savingExcel, setSavingExcel] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // Inline edit state
  const [editingId, setEditingId] = useState(null);
  const [editOem, setEditOem] = useState("");
  const [editModel, setEditModel] = useState("");
  const [editCapacity, setEditCapacity] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!oem.trim() || !model.trim() || !capacity) {
      message.error("Please fill in all fields.");
      return;
    }
    const capNum = parseFloat(capacity);
    if (isNaN(capNum) || capNum <= 0) {
      message.error("Rated Capacity must be a positive number.");
      return;
    }

    setSavingManual(true);
    try {
      await createContainerBatch({
        oem: oem.trim(),
        model: model.trim(),
        rated_capacity_mwh: capNum,
      });
      message.success("BESS Container profile saved successfully!");
      setOem("");
      setModel("");
      setCapacity("");
      if (onSuccess) onSuccess();
    } catch (err) {
      message.error(err.message || "Failed to save BESS Container.");
    } finally {
      setSavingManual(false);
    }
  };

  const handleDownloadTemplate = () => {
    const wsData = [
      ["oem", "model", "rated_capacity_mwh"],
      ["OEM_A", "Model_A", 5.0],
      ["OEM_B", "Model_B", 3.354],
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "BESS Container Template");
    XLSX.writeFile(wb, "bess_container_template.xlsx");
    message.success("Excel Template downloaded successfully.");
  };

  const handleFileParse = (file) => {
    if (!file) return;
    if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
      message.error("Invalid file format. Please upload an Excel (.xlsx or .xls) file.");
      return;
    }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length < 2) {
          throw new Error("Excel sheet is empty or contains no records.");
        }

        const headers = jsonData[0].map((h) => String(h || "").trim().toLowerCase());
        const expectedHeaders = ["oem", "model", "rated_capacity_mwh"];
        const missing = expectedHeaders.filter((h) => !headers.includes(h));
        if (missing.length > 0) {
          throw new Error(`Excel sheet is missing required columns: ${missing.join(", ")}`);
        }

        const oemIdx = headers.indexOf("oem");
        const modelIdx = headers.indexOf("model");
        const capIdx = headers.indexOf("rated_capacity_mwh");

        const items = [];
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (
            !row ||
            row.length === 0 ||
            row.every((cell) => cell === null || cell === undefined || String(cell).trim() === "")
          ) {
            continue;
          }

          const oemVal = row[oemIdx];
          const modelVal = row[modelIdx];
          const capVal = row[capIdx];

          if (oemVal === undefined || modelVal === undefined || capVal === undefined) {
            throw new Error(`Row ${i + 1} has incomplete data.`);
          }

          const cap = parseFloat(capVal);
          if (isNaN(cap) || cap <= 0) {
            throw new Error(`Row ${i + 1} has invalid rated_capacity_mwh (must be a positive number).`);
          }

          items.push({
            oem: String(oemVal).trim(),
            model: String(modelVal).trim(),
            rated_capacity_mwh: cap,
          });
        }

        if (items.length === 0) {
          throw new Error("No valid records found in the Excel file.");
        }

        setParsedData(items);
        message.success(`Successfully parsed ${items.length} records from ${file.name}`);
      } catch (err) {
        message.error(`Parsing failed: ${err.message}`);
        setParsedData([]);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleSaveExcel = async () => {
    setSavingExcel(true);
    let successCount = 0;
    try {
      for (const item of parsedData) {
        await createContainerBatch(item);
        successCount++;
      }
      message.success(`Successfully registered ${successCount} new BESS Container profiles in database!`);
      setParsedData([]);
      setFileName("");
      if (onSuccess) onSuccess();
    } catch (err) {
      message.error(err.message || "Failed to save some profiles.");
    } finally {
      setSavingExcel(false);
    }
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setEditOem(c.oem);
    setEditModel(c.model);
    setEditCapacity(c.rated_capacity_mwh);
  };

  const handleSaveEdit = async (id) => {
    if (!editOem.trim() || !editModel.trim() || !editCapacity) {
      message.error("Fields cannot be empty.");
      return;
    }
    const capNum = parseFloat(editCapacity);
    if (isNaN(capNum) || capNum <= 0) {
      message.error("Capacity must be positive.");
      return;
    }

    setSavingEdit(true);
    try {
      const res = await customFetch(`${BASE_URL}/static/container-batch/${id}/`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          oem: editOem.trim(),
          model: editModel.trim(),
          rated_capacity_mwh: capNum
        })
      });
      if (!res.ok) {
        throw new Error("Failed to update container batch profile");
      }
      message.success("BESS Container updated successfully!");
      setEditingId(null);
      if (onSuccess) onSuccess();
    } catch (err) {
      message.error(err.message || "Failed to update BESS Container.");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteContainer = (c) => {
    const displayName = `${c.oem || ''} - ${c.model || ''}`.trim() || `Container #${c.id}`;
    modal.confirm({
      title: "Delete BESS Container?",
      content: `Are you sure you want to delete container "${displayName}"? This action will also delete associated performance and AUX power data and cannot be undone.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deleteContainerBatch(c.id);
          message.success("BESS Container deleted successfully!");
          if (onSuccess) onSuccess();
        } catch (err) {
          message.error(err.message || "Failed to delete BESS Container.");
        }
      },
    });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 pt-4 animate-fade-in items-stretch">
      {/* Row 1: Left Form, Right Drag & Drop */}
      <div className="lg:col-span-6 flex flex-col h-full">
        <div className="bess-card flex-1">
          <h3 className="headline-sm font-semibold text-slate-800 border-b border-slate-100 pb-3 flex items-center gap-1.5">
            Register New Container
          </h3>
          <form onSubmit={handleManualSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-slate-600">OEM Manufacturer</label>
                <input
                  type="text"
                  required
                  value={oem}
                  onChange={(e) => setOem(e.target.value)}
                  placeholder="e.g. EnergyScale Tech"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-slate-600">Model Identifier</label>
                <input
                  type="text"
                  required
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  placeholder="e.g. ALPHA-X4"
                />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-slate-600 flex items-center">
                Rated Capacity
                <span className="tech-badge">MWH</span>
              </label>
              <input
                type="number"
                step="0.001"
                required
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
                placeholder="5.0"
              />
            </div>
            <button
              type="submit"
              disabled={savingManual}
              className="w-full bess-btn-primary mt-2"
            >
              {savingManual ? <LoadingOutlined /> : <PlusOutlined />} Save Configuration
            </button>
          </form>
        </div>
      </div>

      <div className="lg:col-span-6 flex flex-col h-full">
        <div
          onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={(e) => { e.preventDefault(); setDragActive(false); if (e.dataTransfer.files?.[0]) handleFileParse(e.dataTransfer.files[0]); }}
          className={`bess-upload-zone flex-1 ${dragActive ? "active" : ""}`}
        >
          <div className="w-14 h-14 bg-[#f0f9ff] rounded-full flex items-center justify-center mb-4">
            <UploadOutlined className="text-2xl text-[#006194]" />
          </div>
          <h3 className="headline-sm font-semibold text-slate-800 mb-1">
            Bulk Configuration Import
          </h3>
          <p className="text-xs text-slate-500 max-w-sm text-center mb-5 leading-relaxed">
            Drag and drop your Excel configuration files here or click to browse files from your local system.
          </p>
          <input
            type="file"
            accept=".xlsx, .xls"
            onChange={(e) => { if (e.target.files?.[0]) handleFileParse(e.target.files[0]); }}
            className="hidden"
            id="ctr-file-upload-input"
          />

          <div className="flex flex-col items-center gap-3">
            <label
              htmlFor="ctr-file-upload-input"
              className="h-10 px-4 bg-white hover:text-slate-400 text-xs font-semibold text-slate-600 flex items-center justify-center transition-colors cursor-pointer"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0 !important' }}
            >
              Browse Files
            </label>

            <button
              type="button"
              onClick={handleDownloadTemplate}
              className="bess-btn-download"
            >
              <FileTextOutlined /> Download Template
            </button>
          </div>

          {fileName && (
            <div className="mt-3 text-xs text-slate-600 font-medium">
              Selected: <span className="text-[#006194]">{fileName}</span>
            </div>
          )}

          {parsedData.length > 0 && (
            <div className="border border-slate-100 rounded-lg p-3 bg-slate-50/50 mt-4 w-full max-w-md">
              <div className="flex justify-between items-center text-[10px] font-bold text-slate-500 mb-2">
                <span>Total Records</span>
                <span>{parsedData.length} records</span>
              </div>
              <button
                onClick={handleSaveExcel}
                disabled={savingExcel}
                className="w-full h-9 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition-colors cursor-pointer flex justify-center items-center gap-1.5"
              >
                {savingExcel ? <LoadingOutlined /> : <CheckCircleOutlined />} Save excel records ({parsedData.length})
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Row 2: Bottom Table */}
      <div className="lg:col-span-12 mt-2">
        <div className="bess-card flex flex-col h-full">
          <div className="flex items-center justify-between border-b border-slate-100 pb-4 mb-4">
            <h3 className="headline-sm font-semibold text-slate-800">
              Registered Containers
            </h3>
          </div>

          {loading ? (
            <div className="flex-1 flex flex-col justify-center items-center py-12 text-center text-slate-400 text-xs">
              <LoadingOutlined className="text-xl mb-2 text-[#006194] animate-spin" />
              <div>Loading BESS Container profiles...</div>
            </div>
          ) : containers.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="bess-table">
                <thead>
                  <tr>
                    <th style={{ width: '30%' }}>OEM</th>
                    <th style={{ width: '30%' }}>Model</th>
                    <th style={{ width: '25%' }}>Capacity (MWh)</th>
                    <th className="text-center" style={{ width: '15%' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {containers.map((c) => (
                    editingId === c.id ? (
                      <tr key={c.id}>
                        <td>
                          <input
                            type="text"
                            required
                            value={editOem}
                            onChange={(e) => setEditOem(e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            required
                            value={editModel}
                            onChange={(e) => setEditModel(e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            step="0.001"
                            required
                            value={editCapacity}
                            onChange={(e) => setEditCapacity(e.target.value)}
                          />
                        </td>
                        <td className="text-center">
                          <div className="flex gap-2 justify-center">
                            <button
                              onClick={() => handleSaveEdit(c.id)}
                              disabled={savingEdit}
                              className="bess-action-link"
                            >
                              {savingEdit ? "Saving" : "Save"}
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              className="bess-action-link secondary"
                            >
                              Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <tr key={c.id}>
                        <td className="font-semibold text-slate-700">{c.oem}</td>
                        <td className="text-slate-600">{c.model}</td>
                        <td className="geist-val">{parseFloat(c.rated_capacity_mwh).toFixed(2)}</td>
                        <td className="text-center">
                          <div className="bess-table-actions">
                            <button onClick={() => startEdit(c)} className="bess-action-link">
                              Edit
                            </button>
                            <button onClick={() => handleDeleteContainer(c)} className="bess-action-link danger">
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex-1 flex justify-center items-center py-12 text-center text-slate-400 text-xs italic">
              No container profiles found. Please register one manually or upload.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Helper function to format percentage values (preserves exact decimal precision)
const formatPct = (val, minDecimals = 2) => {
  if (val == null || val === "" || isNaN(val)) return "-";
  const num = parseFloat(val);
  const pct = num > 0 && num <= 1.0 ? num * 100 : num;
  const str = String(val);
  const decimalDigits = str.includes(".") ? str.split(".")[1].length : 0;
  const decimals = Math.max(minDecimals, decimalDigits);
  return `${pct.toFixed(decimals)}%`;
};

// ==========================================
// 2. Container Performance Tab Component
// ==========================================
function ContainerPerformanceTab({ containers, loadingContainers }) {
  const { message, modal } = App.useApp();
  const [selectedBatch, setSelectedBatch] = useState("");
  const [performances, setPerformances] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  // Manual Form inputs
  const [editingId, setEditingId] = useState(null);
  const [year, setYear] = useState("");
  const [soh, setSoh] = useState("");
  const [dod, setDod] = useState("");
  const [calDeg, setCalDeg] = useState("");
  const [chgEff, setChgEff] = useState("");
  const [dischgEff, setDischgEff] = useState("");
  const [yoyDeg, setYoyDeg] = useState("");
  const [dcRte, setDcRte] = useState("");
  const [cycle, setCycle] = useState("");
  const [cRate, setCRate] = useState("");
  const [savingManual, setSavingManual] = useState(false);

  // Excel upload state
  const [parsedData, setParsedData] = useState([]);
  const [fileName, setFileName] = useState("");
  const [savingExcel, setSavingExcel] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const resetForm = () => {
    setEditingId(null);
    setYear("");
    setSoh("");
    setDod("");
    setCalDeg("");
    setChgEff("");
    setDischgEff("");
    setYoyDeg("");
    setDcRte("");
    setCycle("");
    setCRate("");
  };

  const loadPerformances = async (batchId) => {
    if (!batchId) {
      setPerformances([]);
      return;
    }
    try {
      setLoadingList(true);
      const data = await fetchContainerPerformances(batchId);
      data.sort((a, b) => a.year - b.year);
      setPerformances(data);
    } catch (err) {
      message.error("Failed to load performances: " + err.message);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    if (containers.length > 0 && !selectedBatch) {
      setSelectedBatch(containers[0].id);
      loadPerformances(containers[0].id);
    }
  }, [containers]);

  const handleBatchChange = (val) => {
    setSelectedBatch(val);
    resetForm();
    loadPerformances(val);
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setYear(p.year !== undefined && p.year !== null ? String(p.year) : "");
    setSoh(p.soh !== undefined && p.soh !== null ? String(p.soh) : "");
    setDod(p.dod !== undefined && p.dod !== null ? String(p.dod) : "");
    setCalDeg(p.calander_degradation !== undefined && p.calander_degradation !== null ? String(p.calander_degradation) : "");
    setChgEff(p.charge_efficiency !== undefined && p.charge_efficiency !== null ? String(p.charge_efficiency) : "");
    setDischgEff(p.discharge_efficiency !== undefined && p.discharge_efficiency !== null ? String(p.discharge_efficiency) : "");
    setYoyDeg(p.yoy_degradation !== undefined && p.yoy_degradation !== null ? String(p.yoy_degradation) : "");
    setDcRte(p.dc_rte !== undefined && p.dc_rte !== null ? String(p.dc_rte) : "");
    setCycle(p.cycle !== undefined && p.cycle !== null ? String(p.cycle) : "");
    setCRate(p.c_rate !== undefined && p.c_rate !== null ? String(p.c_rate) : "");
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!selectedBatch) {
      message.error("Please select a Container Batch first.");
      return;
    }
    if (
      year === "" || year === null || year === undefined ||
      soh === "" ||
      dod === "" ||
      calDeg === "" ||
      chgEff === "" ||
      dischgEff === "" ||
      yoyDeg === "" ||
      dcRte === ""
    ) {
      message.error("Please fill in all required fields.");
      return;
    }

    setSavingManual(true);
    try {
      const payload = {
        container_batch: Number(selectedBatch),
        year: parseInt(year, 10),
        soh: parseFloat(soh),
        dod: parseFloat(dod),
        calander_degradation: parseFloat(calDeg),
        charge_efficiency: parseFloat(chgEff),
        discharge_efficiency: parseFloat(dischgEff),
        yoy_degradation: parseFloat(yoyDeg),
        dc_rte: parseFloat(dcRte),
        cycle: cycle !== "" && cycle !== null && cycle !== undefined ? parseFloat(cycle) : null,
        c_rate: cRate !== "" && cRate !== null && cRate !== undefined ? parseFloat(cRate) : null,
      };

      if (editingId) {
        await updateContainerPerformance(editingId, payload);
        message.success("Performance record updated successfully!");
      } else {
        await createContainerPerformance(payload);
        message.success("Performance record saved successfully!");
      }
      resetForm();
      loadPerformances(selectedBatch);
    } catch (err) {
      message.error(err.message || (editingId ? "Failed to update performance record." : "Failed to save performance record."));
    } finally {
      setSavingManual(false);
    }
  };

  const handleDownloadTemplate = () => {
    const headers = [
      "year",
      "soh (%)",
      "dod (%)",
      "calander_degradation (%)",
      "charge_efficiency (%)",
      "discharge_efficiency (%)",
      "yoy_degradation (%)",
      "dc_rte (%)",
      "cycle (opt)",
      "c_rate (opt)",
    ];
    const wsData = [headers];
    for (let yr = 0; yr <= 25; yr++) {
      const sohVal = parseFloat((100.0 - yr * 1.2).toFixed(2));
      wsData.push([yr, sohVal, 80.0, 0.5, 98.5, 98.5, 1.2, 95.0, 1.0, 0.5]);
    }
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Performance Template");
    XLSX.writeFile(wb, "container_performance_template.xlsx");
    message.success("Excel Template downloaded successfully.");
  };

  const handleFileParse = (file) => {
    if (!file) return;
    if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
      message.error("Invalid file format. Please upload an Excel (.xlsx or .xls) file.");
      return;
    }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length < 2) {
          throw new Error("Excel sheet is empty.");
        }

        const mapHeaderKey = (rawStr) => {
          let s = String(rawStr || "").trim().toLowerCase().replace(/[\%\(\)]/g, "").trim();
          if (s === "year" || s.includes("year") || s.includes("yr")) return "year";
          if (s.includes("soh")) return "soh";
          if (s.includes("dod")) return "dod";
          if (s.includes("cal") || s.includes("calander")) return "calander_degradation";
          if (s.includes("dischg") || s.includes("discharge")) return "discharge_efficiency";
          if (s.includes("chg") || s.includes("charge")) return "charge_efficiency";
          if (s.includes("yoy")) return "yoy_degradation";
          if (s.includes("dc rte") || s.includes("dc_rte") || s.includes("rte")) return "dc_rte";
          if (s.includes("cycle")) return "cycle";
          if (s.includes("crate") || s.includes("c-rate") || s.includes("c_rate")) return "c_rate";
          return s.replace(/\s+/g, "_");
        };

        const rawHeaders = jsonData[0];
        const mappedKeys = rawHeaders.map(mapHeaderKey);
        const requiredKeys = ["year", "soh", "dod", "calander_degradation", "charge_efficiency", "discharge_efficiency", "yoy_degradation", "dc_rte"];
        const missing = requiredKeys.filter((k) => !mappedKeys.includes(k));
        if (missing.length > 0) {
          throw new Error(`Excel is missing columns: ${missing.join(", ")}`);
        }

        const items = [];
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (!row || row.length === 0 || row.every((c) => c === null || c === undefined || String(c).trim() === "")) continue;

          const item = {};
          mappedKeys.forEach((key, idx) => {
            if (row[idx] !== undefined && row[idx] !== null && String(row[idx]).trim() !== "") {
              item[key] = parseFloat(row[idx]);
            } else {
              item[key] = null;
            }
          });

          items.push(item);
        }

        setParsedData(items);
        message.success(`Successfully parsed ${items.length} performance records.`);
      } catch (err) {
        message.error(`Parsing failed: ${err.message}`);
        setParsedData([]);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleSaveExcel = async () => {
    if (!selectedBatch) {
      message.error("Please select a BESS Container.");
      return;
    }
    setSavingExcel(true);
    try {
      const ws = XLSX.utils.json_to_sheet(parsedData);
      const csvContent = XLSX.utils.sheet_to_csv(ws);
      const file = new File([csvContent], "file.csv", { type: "text/csv" });

      await uploadContainerPerformance(selectedBatch, file);
      message.success("Successfully uploaded Container Performance curve!");
      setParsedData([]);
      setFileName("");
      loadPerformances(selectedBatch);
    } catch (err) {
      message.error(err.message || "Failed to upload performance curve.");
    } finally {
      setSavingExcel(false);
    }
  };

  const handleDeletePerfRow = (p) => {
    modal.confirm({
      title: "Delete Performance Record?",
      content: `Are you sure you want to delete Year ${p.year} performance record (SoH: ${p.soh}%)?`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deleteContainerPerformance(p.id);
          message.success("Performance record deleted successfully!");
          if (editingId === p.id) {
            resetForm();
          }
          loadPerformances(selectedBatch);
        } catch (err) {
          message.error(err.message || "Failed to delete performance record.");
        }
      },
    });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 pt-4 animate-fade-in items-stretch">
      {/* Left List & Selector */}
      <div className="lg:col-span-7 flex flex-col h-full">
        <div className="bess-card flex-1">
          <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-slate-100 pb-3 gap-3 mb-4">
            <h3 className="headline-sm font-semibold text-slate-800">
              Performance Curve (25-Year)
            </h3>
            <Select
              value={selectedBatch || undefined}
              disabled={loadingContainers}
              onChange={(val) => handleBatchChange(val)}
              placeholder="Select BESS Container..."
              className="bess-antd-select max-w-xs w-full"
              style={{ width: '100%', maxWidth: '320px' }}
            >
              {containers.map((c) => (
                <Select.Option key={c.id} value={c.id}>
                  {c.oem} - {c.model} ({parseFloat(c.rated_capacity_mwh).toFixed(2)} MWh)
                </Select.Option>
              ))}
            </Select>
          </div>

          {loadingList ? (
            <div className="flex-1 flex flex-col justify-center items-center py-12 text-center text-slate-400 text-xs">
              <LoadingOutlined className="text-xl mb-2 text-[#006194] animate-spin" />
              <div>Loading performance curves...</div>
            </div>
          ) : performances.length > 0 ? (
            <div className="overflow-x-auto max-h-[850px]">
              <table className="bess-table">
                <thead>
                  <tr>
                    <th>Year</th>
                    <th style={{ textAlign: 'right' }}>SoH (%)</th>
                    <th style={{ textAlign: 'right' }}>DoD (%)</th>
                    <th style={{ textAlign: 'right' }}>Cal Deg (%)</th>
                    <th style={{ textAlign: 'right' }}>Chg Eff (%)</th>
                    <th style={{ textAlign: 'right' }}>Dischg Eff (%)</th>
                    <th style={{ textAlign: 'right' }}>YoY Deg (%)</th>
                    <th style={{ textAlign: 'right' }}>DC RTE (%)</th>
                    <th className="text-center bess-sticky-col" style={{ width: '130px', minWidth: '130px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {performances.map((p) => (
                    <tr key={p.id} className={editingId === p.id ? "bg-sky-50/70" : ""}>
                      <td className="font-semibold text-slate-700">Year {p.year}</td>
                      <td className="text-right geist-val">{formatPct(p.soh, 2)}</td>
                      <td className="text-right geist-val">{formatPct(p.dod, 1)}</td>
                      <td className="text-right geist-val">{formatPct(p.calander_degradation, 2)}</td>
                      <td className="text-right geist-val">{formatPct(p.charge_efficiency, 2)}</td>
                      <td className="text-right geist-val">{formatPct(p.discharge_efficiency, 2)}</td>
                      <td className="text-right geist-val">{formatPct(p.yoy_degradation, 2)}</td>
                      <td className="text-right geist-val font-semibold text-slate-800">{formatPct(p.dc_rte, 2)}</td>
                      <td className="text-center bess-sticky-col">
                        <div className="bess-table-actions">
                          <button
                            onClick={() => startEdit(p)}
                            className={`bess-action-link ${editingId === p.id ? "font-bold text-[#006194]" : ""}`}
                          >
                            {editingId === p.id ? "Editing..." : "Edit"}
                          </button>
                          <button onClick={() => handleDeletePerfRow(p)} className="bess-action-link danger">
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex-1 flex justify-center items-center py-12 text-center text-slate-400 text-xs italic">
              No performance curve defined for the selected container. Add records manually or upload an Excel.
            </div>
          )}
        </div>
      </div>

      {/* Right Form & Upload */}
      <div className="lg:col-span-5 flex flex-col gap-6">
        <div
          onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={(e) => { e.preventDefault(); setDragActive(false); if (e.dataTransfer.files?.[0]) handleFileParse(e.dataTransfer.files[0]); }}
          className={`bess-upload-zone ${dragActive ? "active" : ""}`}
        >
          <UploadOutlined className="text-2xl mb-2 text-[#006194]" />
          <h4 className="font-semibold text-slate-800 mb-1 text-sm">
            Bulk Upload Performance Curve
          </h4>
          <p className="text-xs text-slate-500 mb-4 max-w-xs leading-relaxed">
            Drag & Drop Curve Excel here or select browse
          </p>
          <input
            type="file"
            accept=".xlsx, .xls"
            onChange={(e) => { if (e.target.files?.[0]) handleFileParse(e.target.files[0]); }}
            className="hidden"
            id="perf-file-upload-input"
          />

          <div className="flex flex-col items-center gap-2">
            <label
              htmlFor="perf-file-upload-input"
              className="h-9 px-3 bg-white hover:text-slate-400 border border-slate-200 rounded text-xs font-semibold text-slate-600 flex items-center justify-center transition-colors cursor-pointer"
              style={{ marginBottom: '0 !important' }}
            >
              Browse Files
            </label>
            <button
              type="button"
              onClick={handleDownloadTemplate}
              className="bess-btn-download h-8 text-[11px]"
            >
              <FileTextOutlined /> Download Template
            </button>
          </div>

          {fileName && (
            <div className="mt-2 text-xs text-slate-600">
              Selected: <span className="text-[#006194]">{fileName}</span>
            </div>
          )}

          {parsedData.length > 0 && (
            <div className="border border-slate-100 rounded-lg p-3 bg-slate-50/50 mt-3 w-full">
              <div className="flex justify-between items-center text-[10px] font-bold text-slate-500 mb-2">
                <span>Total Records</span>
                <span>{parsedData.length} records</span>
              </div>
              <button
                onClick={handleSaveExcel}
                disabled={savingExcel}
                className="w-full h-8 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-bold transition-colors cursor-pointer flex justify-center items-center gap-1.5"
              >
                {savingExcel ? <LoadingOutlined /> : <CheckCircleOutlined />} Save Curve
              </button>
            </div>
          )}
        </div>

        <div className="bess-card">
          <div className="border-b border-slate-100 pb-3 flex items-center justify-between">
            <h3 className="headline-sm font-semibold text-slate-800">
              {editingId ? "Update Performance Row" : "Add Performance Row"}
            </h3>
          </div>
          <form onSubmit={handleManualSubmit} className="space-y-3 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-slate-600">Year</label>
                <input
                  type="number"
                  required
                  min="0"
                  max="50"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  placeholder="e.g. 0"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-slate-600">SoH (%)</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={soh}
                  onChange={(e) => setSoh(e.target.value)}
                  placeholder="e.g. 100"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-slate-600">DoD (%)</label>
                <input
                  type="number"
                  step="0.1"
                  required
                  value={dod}
                  onChange={(e) => setDod(e.target.value)}
                  placeholder="e.g. 80.0"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-slate-600">Cal Deg (%)</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={calDeg}
                  onChange={(e) => setCalDeg(e.target.value)}
                  placeholder="e.g. 0.5"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-slate-600">Chg Eff (%)</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={chgEff}
                  onChange={(e) => setChgEff(e.target.value)}
                  placeholder="e.g. 98.5"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-slate-600">Dischg Eff (%)</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={dischgEff}
                  onChange={(e) => setDischgEff(e.target.value)}
                  placeholder="e.g. 98.5"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-slate-600">YoY Deg (%)</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={yoyDeg}
                  onChange={(e) => setYoyDeg(e.target.value)}
                  placeholder="e.g. 1.2"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-slate-600">DC RTE (%)</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={dcRte}
                  onChange={(e) => setDcRte(e.target.value)}
                  placeholder="e.g. 95.0"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-slate-600">Cycle (opt)</label>
                <input
                  type="number"
                  step="0.01"
                  value={cycle}
                  onChange={(e) => setCycle(e.target.value)}
                  placeholder="e.g. 1.0"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-slate-600">C-Rate (opt)</label>
                <input
                  type="number"
                  step="0.01"
                  value={cRate}
                  onChange={(e) => setCRate(e.target.value)}
                  placeholder="e.g. 0.5"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-2">
              <button
                type="submit"
                disabled={savingManual}
                className="flex-1 bess-btn-primary"
              >
                {savingManual ? <LoadingOutlined /> : (editingId ? <CheckOutlined /> : <PlusOutlined />)} {editingId ? "Update Performance Row" : "Add Performance Row"}
              </button>
              {editingId && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-3 h-9 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded text-xs font-semibold transition-colors"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ==========================================
// 3. Container AUX Power Tab Component
// ==========================================
function ContainerAuxPowerTab({ containers, loadingContainers }) {
  const { message, modal } = App.useApp();
  const [selectedBatch, setSelectedBatch] = useState("");
  const [auxRecords, setAuxRecords] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  // Manual Form inputs
  const [ambientTemp, setAmbientTemp] = useState("");
  const [operation, setOperation] = useState("");
  const [standby, setStandby] = useState("");
  const [savingManual, setSavingManual] = useState(false);

  // Inline Edit State
  const [editingId, setEditingId] = useState(null);
  const [editTemp, setEditTemp] = useState("");
  const [editOp, setEditOp] = useState("");
  const [editStandby, setEditStandby] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  // Excel upload state
  const [parsedData, setParsedData] = useState([]);
  const [fileName, setFileName] = useState("");
  const [savingExcel, setSavingExcel] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const loadAuxRecords = async (batchId) => {
    if (!batchId) {
      setAuxRecords([]);
      return;
    }
    try {
      setLoadingList(true);
      const data = await fetchContainerAuxPowers(batchId);
      data.sort((a, b) => a.ambient_temp_c - b.ambient_temp_c);
      setAuxRecords(data);
    } catch (err) {
      message.error("Failed to load auxiliary power profiles: " + err.message);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    if (containers.length > 0 && !selectedBatch) {
      setSelectedBatch(containers[0].id);
      loadAuxRecords(containers[0].id);
    }
  }, [containers]);

  const handleBatchChange = (val) => {
    setSelectedBatch(val);
    loadAuxRecords(val);
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!selectedBatch) {
      message.error("Please select a Container Batch first.");
      return;
    }
    if (ambientTemp === "" || operation === "" || standby === "") {
      message.error("Please fill in all fields.");
      return;
    }

    setSavingManual(true);
    try {
      await createContainerAuxPower({
        container: Number(selectedBatch),
        ambient_temp_c: parseFloat(ambientTemp),
        operation: parseFloat(operation),
        standby: parseFloat(standby),
      });
      message.success("Auxiliary Power row saved successfully!");
      setAmbientTemp("");
      setOperation("");
      setStandby("");
      loadAuxRecords(selectedBatch);
    } catch (err) {
      message.error(err.message || "Failed to save auxiliary power row.");
    } finally {
      setSavingManual(false);
    }
  };

  const handleDownloadTemplate = () => {
    const wsData = [
      ["ambient_temp_c", "operation", "standby"],
      [25.0, 15.5, 2.5],
      [35.0, 22.0, 3.8],
      [45.0, 28.5, 5.0],
      [50.0, 32.0, 6.2],
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "AUX Power Template");
    XLSX.writeFile(wb, "container_aux_power_template.xlsx");
    message.success("Excel Template downloaded successfully.");
  };

  const handleFileParse = (file) => {
    if (!file) return;
    if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
      message.error("Invalid file format. Please upload an Excel (.xlsx or .xls) file.");
      return;
    }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length < 2) {
          throw new Error("Excel sheet is empty.");
        }

        const cleanedHeaders = jsonData[0].map((h) => String(h || "").trim().toLowerCase().replace(/[\%\(\)]/g, "").trim());
        const tempIdx = cleanedHeaders.findIndex(h => h.includes("temp") || h.includes("ambient"));
        const opIdx = cleanedHeaders.findIndex(h => h.includes("operation") || h.includes("op"));
        const standbyIdx = cleanedHeaders.findIndex(h => h.includes("standby"));

        const missing = [];
        if (tempIdx === -1) missing.push("ambient_temp_c");
        if (opIdx === -1) missing.push("operation");
        if (standbyIdx === -1) missing.push("standby");

        if (missing.length > 0) {
          throw new Error(`Excel is missing columns: ${missing.join(", ")}`);
        }

        const items = [];
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (!row || row.length === 0 || row.every((c) => c === null || c === undefined || String(c).trim() === "")) continue;

          items.push({
            ambient_temp_c: parseFloat(row[tempIdx]),
            operation: parseFloat(row[opIdx]),
            standby: parseFloat(row[standbyIdx]),
          });
        }

        setParsedData(items);
        message.success(`Successfully parsed ${items.length} auxiliary power rows.`);
      } catch (err) {
        message.error(`Parsing failed: ${err.message}`);
        setParsedData([]);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleSaveExcel = async () => {
    if (!selectedBatch) {
      message.error("Please select a BESS Container.");
      return;
    }
    setSavingExcel(true);
    try {
      const file = new File(
        [
          (() => {
            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(parsedData);
            XLSX.utils.book_append_sheet(wb, ws, "sheet1");
            return XLSX.write(wb, { bookType: "xlsx", type: "array" });
          })(),
        ],
        "file.xlsx"
      );

      await uploadContainerAuxPower(selectedBatch, file);
      message.success("Successfully uploaded Container AUX Power profile!");
      setParsedData([]);
      setFileName("");
      loadAuxRecords(selectedBatch);
    } catch (err) {
      message.error(err.message || "Failed to upload auxiliary power curve.");
    } finally {
      setSavingExcel(false);
    }
  };

  const startEdit = (r) => {
    setEditingId(r.id);
    setEditTemp(r.ambient_temp_c);
    setEditOp(r.operation);
    setEditStandby(r.standby);
  };

  const handleSaveEdit = async (id) => {
    if (editTemp === "" || editOp === "" || editStandby === "") {
      message.error("Please fill in all fields.");
      return;
    }
    const tempVal = parseFloat(editTemp);
    const opVal = parseFloat(editOp);
    const stbyVal = parseFloat(editStandby);
    if (isNaN(tempVal) || isNaN(opVal) || isNaN(stbyVal) || opVal < 0 || stbyVal < 0) {
      message.error("Invalid input values.");
      return;
    }

    setSavingEdit(true);
    try {
      await updateContainerAuxPower(id, {
        container: selectedBatch,
        ambient_temp_c: tempVal,
        operation: opVal,
        standby: stbyVal,
      });
      message.success("Auxiliary Power row updated successfully!");
      setEditingId(null);
      loadAuxRecords(selectedBatch);
    } catch (err) {
      message.error(err.message || "Failed to update auxiliary power row.");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteAuxRow = (r) => {
    modal.confirm({
      title: "Delete AUX Power Row?",
      content: `Are you sure you want to delete row for ${parseFloat(r.ambient_temp_c).toFixed(1)}°C (Operation: ${parseFloat(r.operation).toFixed(3)} kW, Standby: ${parseFloat(r.standby).toFixed(3)} kW)?`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deleteContainerAuxPower(r.id);
          message.success("Auxiliary power row deleted successfully!");
          loadAuxRecords(selectedBatch);
        } catch (err) {
          message.error(err.message || "Failed to delete auxiliary power row.");
        }
      },
    });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 pt-4 animate-fade-in items-stretch">
      {/* Left List & Selector */}
      <div className="lg:col-span-7 flex flex-col h-full">
        <div className="bess-card flex-1">
          <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-slate-100 pb-3 gap-3 mb-4">
            <h3 className="headline-sm font-semibold text-slate-800">
              Auxiliary Power Profile
            </h3>
            <Select
              value={selectedBatch || undefined}
              disabled={loadingContainers}
              onChange={(val) => handleBatchChange(val)}
              placeholder="Select BESS Container..."
              className="bess-antd-select max-w-xs w-full"
              style={{ width: '100%', maxWidth: '320px' }}
            >
              {containers.map((c) => (
                <Select.Option key={c.id} value={c.id}>
                  {c.oem} - {c.model} ({parseFloat(c.rated_capacity_mwh).toFixed(2)} MWh)
                </Select.Option>
              ))}
            </Select>
          </div>

          {loadingList ? (
            <div className="flex-1 flex flex-col justify-center items-center py-12 text-center text-slate-400 text-xs">
              <LoadingOutlined className="text-xl mb-2 text-[#006194] animate-spin" />
              <div>Loading auxiliary power profiles...</div>
            </div>
          ) : auxRecords.length > 0 ? (
            <div className="overflow-x-auto max-h-[850px]">
              <table className="bess-table">
                <thead>
                  <tr>
                    <th>Ambient Temp (°C)</th>
                    <th style={{ textAlign: 'right' }}>Operation (kW)</th>
                    <th style={{ textAlign: 'right' }}>Standby (kW)</th>
                    <th className="text-center" style={{ width: '130px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {auxRecords.map((r) => (
                    editingId === r.id ? (
                      <tr key={r.id}>
                        <td>
                          <input
                            type="number"
                            step="0.1"
                            required
                            value={editTemp}
                            onChange={(e) => setEditTemp(e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            step="0.00001"
                            required
                            value={editOp}
                            onChange={(e) => setEditOp(e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            step="0.00001"
                            required
                            value={editStandby}
                            onChange={(e) => setEditStandby(e.target.value)}
                          />
                        </td>
                        <td className="text-center">
                          <div className="flex gap-2 justify-center">
                            <button
                              onClick={() => handleSaveEdit(r.id)}
                              disabled={savingEdit}
                              className="bess-action-link"
                            >
                              {savingEdit ? "Saving" : "Save"}
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              className="bess-action-link secondary"
                            >
                              Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <tr key={r.id}>
                        <td className="font-semibold text-slate-700">{parseFloat(r.ambient_temp_c).toFixed(1)} °C</td>
                        <td className="text-right geist-val">{parseFloat(r.operation).toFixed(3)} kW</td>
                        <td className="text-right geist-val font-semibold text-slate-800">{parseFloat(r.standby).toFixed(3)} kW</td>
                        <td className="text-center">
                          <div className="bess-table-actions">
                            <button onClick={() => startEdit(r)} className="bess-action-link">
                              Edit
                            </button>
                            <button onClick={() => handleDeleteAuxRow(r)} className="bess-action-link danger">
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex-1 flex justify-center items-center py-12 text-center text-slate-400 text-xs italic">
              No auxiliary power profiles found for selected container batch. Add records manually or upload an Excel.
            </div>
          )}
        </div>
      </div>

      {/* Right Form & Upload */}
      <div className="lg:col-span-5 flex flex-col gap-6">
        <div
          onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={(e) => { e.preventDefault(); setDragActive(false); if (e.dataTransfer.files?.[0]) handleFileParse(e.dataTransfer.files[0]); }}
          className={`bess-upload-zone ${dragActive ? "active" : ""}`}
        >
          <UploadOutlined className="text-2xl mb-2 text-[#006194]" />
          <h4 className="font-semibold text-slate-800 mb-1 text-sm">
            Bulk Upload AUX Power
          </h4>
          <p className="text-xs text-slate-500 mb-4 max-w-xs leading-relaxed">
            Drag & Drop AUX Excel here or select browse
          </p>
          <input
            type="file"
            accept=".xlsx, .xls"
            onChange={(e) => { if (e.target.files?.[0]) handleFileParse(e.target.files[0]); }}
            className="hidden"
            id="aux-file-upload-input"
          />

          <div className="flex flex-col items-center gap-2">
            <label
              htmlFor="aux-file-upload-input"
              className="h-9 px-3 bg-white hover:text-slate-400 border border-slate-200 rounded text-xs font-semibold text-slate-600 flex items-center justify-center transition-colors cursor-pointer"
              style={{ marginBottom: '0 !important' }}
            >
              Browse Files
            </label>
            <button
              type="button"
              onClick={handleDownloadTemplate}
              className="bess-btn-download h-8 text-[11px]"
            >
              <FileTextOutlined /> Download Template
            </button>
          </div>

          {fileName && (
            <div className="mt-2 text-xs text-slate-600">
              Selected: <span className="text-[#006194]">{fileName}</span>
            </div>
          )}

          {parsedData.length > 0 && (
            <div className="border border-slate-100 rounded-lg p-3 bg-slate-50/50 mt-3 w-full">
              <div className="flex justify-between items-center text-[10px] font-bold text-slate-500 mb-2">
                <span>Total Records</span>
                <span>{parsedData.length} records</span>
              </div>
              <button
                onClick={handleSaveExcel}
                disabled={savingExcel}
                className="w-full h-8 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-bold transition-colors cursor-pointer flex justify-center items-center gap-1.5"
              >
                {savingExcel ? <LoadingOutlined /> : <CheckCircleOutlined />} Save AUX Power Profile
              </button>
            </div>
          )}
        </div>

        <div className="bess-card">
          <h3 className="headline-sm font-semibold text-slate-800 border-b border-slate-100 pb-3 flex items-center gap-1.5">
            Add AUX Power Profile Row
          </h3>
          <form onSubmit={handleManualSubmit} className="space-y-4 mt-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-slate-600">Ambient Temperature (°C)</label>
              <input
                type="number"
                step="0.1"
                required
                value={ambientTemp}
                onChange={(e) => setAmbientTemp(e.target.value)}
                placeholder="e.g. 25"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-slate-600">Operation (kW)</label>
                <input
                  type="number"
                  step="0.00001"
                  required
                  value={operation}
                  onChange={(e) => setOperation(e.target.value)}
                  placeholder="e.g. 15.5"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-slate-600">Standby (kW)</label>
                <input
                  type="number"
                  step="0.00001"
                  required
                  value={standby}
                  onChange={(e) => setStandby(e.target.value)}
                  placeholder="e.g. 2.5"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={savingManual}
              className="w-full bess-btn-primary mt-2"
            >
              {savingManual ? <LoadingOutlined /> : <PlusOutlined />} Add AUX Row
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
